import { CheckIcon, CopyIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const COPY_CONFIRMATION_MS = 2000;

type CopyState = 'idle' | 'copied' | 'failed';

// Both glyphs stay mounted and cross-fade, so the swap reads as one control changing state.
const ICON_SWAP =
  'absolute inset-0 m-auto transition-[opacity,scale,filter] duration-200 ease-[cubic-bezier(0.2,0,0,1)]';

interface CommandBlockProps {
  readonly command: string;
  /** What the command does, e.g. "install command"; names the copy button. */
  readonly label: string;
  /**
   * `command` is a terminal line behind a `$`; `prompt` is a sentence to give an
   * agent, set in reading type and wrapped rather than scrolled.
   */
  readonly kind?: 'command' | 'prompt';
}

/** Text to copy, behind a prompt glyph for commands, with a copy button that confirms in place. */
export function CommandBlock({ command, label, kind = 'command' }: CommandBlockProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');

  useEffect(() => {
    if (copyState !== 'copied') {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setCopyState('idle');
    }, COPY_CONFIRMATION_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [copyState]);

  async function copyCommand(): Promise<void> {
    try {
      await navigator.clipboard.writeText(command);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }

  const copied = copyState === 'copied';
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-2 rounded-lg bg-muted/60 py-1 pr-1 pl-3 ring-1 ring-foreground/10">
        {kind === 'prompt' ? (
          <q className="min-w-0 flex-1 py-1 text-sm font-normal leading-snug text-pretty text-foreground">
            {command}
          </q>
        ) : (
          <>
            <span aria-hidden="true" className="font-mono text-sm text-muted-foreground select-none">
              $
            </span>
            <code className="min-w-0 flex-1 overflow-x-auto font-mono text-sm whitespace-nowrap text-foreground">
              {command}
            </code>
          </>
        )}
        <Button
          aria-label={`Copy ${label}`}
          className="relative shrink-0"
          onClick={() => {
            void copyCommand();
          }}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          <CopyIcon
            aria-hidden="true"
            className={cn(ICON_SWAP, copied ? 'scale-25 opacity-0 blur-[4px]' : 'scale-100 opacity-100 blur-none')}
            data-icon="standalone"
          />
          <CheckIcon
            aria-hidden="true"
            data-icon="standalone"
            className={cn(
              ICON_SWAP,
              'text-status-success',
              copied ? 'scale-100 opacity-100 blur-none' : 'scale-25 opacity-0 blur-[4px]',
            )}
          />
        </Button>
      </div>
      <p aria-live="polite" className="sr-only">
        {copied ? `Copied ${label}` : null}
      </p>
      {copyState === 'failed' ? (
        <p className="text-xs text-muted-foreground">
          Copying was blocked. Select the command and copy it manually.
        </p>
      ) : null}
    </div>
  );
}
